import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AuthForms from './AuthForms';
import { profileService } from '../../services/profileService';
import ThemeToggle from '../layout/ThemeToggle';
import { useTheme } from '../../context/ThemeContext';

interface LoginPageProps {
  initialMode?: 'login' | 'register';
  redirectUrl?: string;
}

const LoginPage: React.FC<LoginPageProps> = ({ 
  initialMode = 'login',
  redirectUrl = '/'
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const { state: themeState } = useTheme();
  const isDark = themeState.mode === 'dark';

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const isAuth = await profileService.isAuthenticated();
        setIsAuthenticated(isAuth);
        
        // If already authenticated, redirect to dashboard
        if (isAuth) {
          window.location.href = '/';
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col transition-colors duration-200">
        {/* Navbar - Always dark blue in both light and dark modes */}
        <nav className="bg-blue-800 text-white shadow-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center">
                  <img 
                    src={`${process.env.PUBLIC_URL}/cape-icon.svg`}
                    alt="CAPE Trading Strategy" 
                    className="h-10 w-10 mr-2" 
                  />
                  <span className="text-white text-xl font-bold">CAPE Trading</span>
                </div>
                <div className="hidden md:block">
                  <div className="ml-10 flex items-baseline space-x-4">
                    <Link
                      to="/"
                      className="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/about"
                      className="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
                    >
                      About
                    </Link>
                  </div>
                </div>
              </div>
              <div className="flex items-center">
                <ThemeToggle />
              </div>
            </div>
          </div>
        </nav>
        
        <div className="max-w-md mx-auto mt-16 px-4">
          <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Already Signed In</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">You are already signed in.</p>
              <a
                href="/"
                className="inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Go to Dashboard
              </a>
            </div>
          </div>
        </div>
        
        {/* Footer - Always dark blue in both light and dark modes */}
        <footer className="bg-blue-800 text-white py-4 mt-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-sm mb-4 md:mb-0">
                &copy; {new Date().getFullYear()} CAPE Trading Strategy. All rights reserved.
              </div>
              <div className="flex space-x-4">
                <Link to="/about#disclaimer" className="text-white hover:text-gray-200 text-sm font-medium">
                  Financial Disclaimer
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col transition-colors duration-200">
      {/* Navbar - Always dark blue in both light and dark modes */}
      <nav className="bg-blue-800 text-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <img 
                  src={`${process.env.PUBLIC_URL}/cape-icon.svg`}
                  alt="CAPE Trading Strategy" 
                  className="h-10 w-10 mr-2" 
                />
                <span className="text-white text-xl font-bold">CAPE Trading</span>
              </div>
              <div className="hidden md:block">
                <div className="ml-10 flex items-baseline space-x-4">
                  <Link
                    to="/"
                    className="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/about"
                    className="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
                  >
                    About
                  </Link>
                </div>
              </div>
            </div>
            <div className="flex items-center">
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-grow flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h1 className="text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Triple Point Trading Strategy
          </h1>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-300">
            Sign in to access your trading dashboard and portfolio
          </p>
        </div>

        <div className="mt-8">
          <AuthForms redirectUrl={redirectUrl || "/"} initialMode={initialMode} />
        </div>
      </div>
      
      {/* Footer - Always dark blue in both light and dark modes */}
      <footer className="bg-blue-800 text-white py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm mb-4 md:mb-0">
              &copy; {new Date().getFullYear()} CAPE Trading Strategy. All rights reserved.
            </div>
            <div className="flex space-x-4">
              <Link to="/about#disclaimer" className="text-white hover:text-gray-200 text-sm font-medium">
                Financial Disclaimer
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage; 